/*
  Корешок обложки — единый источник разметки и математики для редактора и печати.
  Не зависит от Vue: этот же расчёт зеркалится в functions (PrintCoverSubtask).
*/

import { renderSpineLogo, SPINE_LOGO_ASPECT } from './spineLogo'
import { escapeHtml } from '@/utils/escapeHtml'

const PT_TO_MM = 0.352778
const SPINE_PADDING_MM = 1.6
const PRINT_TEXT_PADDING_MM = 3.4
const BLEED_MM = 3
const MIN_PAGES_FOR_SPINE_TEXT = 80
const MAX_TITLE_LINES = 2
const AUTHOR_FONT_RATIO = 0.7

const DEFAULT_TEXT_COLOR = '#000000'
const DEFAULT_BG_COLOR = '#ffffff'

/* Двузначные числа в вертикальном тексте набираются «в клетку» (縦中横). */
function processNumbers (text) {
  if (!text) return ''
  return String(text).replaceAll(/(\D|^)(\d\d)(\D|$)/g, (_match, p1, p2, p3) => `${p1}<span class="semi-wide-number">${p2}</span>${p3}`)
}

class Spine {
  widthMm
  title
  fontSizePt
  authorName
  canPrintText
  textColor
  bgColor
  bleedMm

  constructor (data = {}) {
    this.widthMm = data.widthMm || 0
    this.title = data.title || ''
    this.fontSizePt = data.fontSizePt ?? null
    this.authorName = data.authorName || ''
    this.canPrintText = data.canPrintText ?? false
    this.textColor = data.textColor || DEFAULT_TEXT_COLOR
    this.bgColor = data.bgColor || DEFAULT_BG_COLOR
    this.bleedMm = data.bleedMm ?? BLEED_MM
  }

  /* Кегль по умолчанию — вся ширина корешка за вычетом полей. */
  get defaultFontSizeMm () {
    return this.widthMm - SPINE_PADDING_MM * 2
  }

  get fontSizeMm () {
    return this.fontSizePt ? this.fontSizePt * PT_TO_MM : this.defaultFontSizeMm
  }

  get titleLines () {
    if (!this.canPrintText) return []
    return String(this.title || '').split('\n').slice(0, MAX_TITLE_LINES)
  }

  get titleLineHeightMm () {
    return this.defaultFontSizeMm / Math.max(1, this.titleLines.length)
  }

  /* Строки не помещаются по ширине корешка. */
  get hasFontSizeError () {
    return this.fontSizeMm > this.titleLineHeightMm + 0.01
  }

  renderLayout () {
    // Тексты и лого на корешке — только от 80 страниц: на тонком корешке
    // размеры уходят в отрицательные значения, и контент физически не помещается.
    if (!this.canPrintText) {
      return '<div class="cover-spine"></div>'
    }

    const titleHtml = `<div class="spine-title${this.hasFontSizeError ? ' error-title' : ''}">${
      this.titleLines.map((line) => `<span>${processNumbers(escapeHtml(line))}</span>`).join('')
    }</div>`

    return `
      <div class="cover-spine">
        <div class="spine-content">
          ${titleHtml}
          <div class="spine-bottom">
            <div class="spine-author">${processNumbers(escapeHtml(this.authorName))}</div>
            ${renderSpineLogo(this.textColor, this.bgColor)}
          </div>
        </div>
      </div>
    `
  }

  renderStyle (prefix) {
    const contentPaddingMm = this.bleedMm + PRINT_TEXT_PADDING_MM
    // Горизонтальный паддинг — отступ текста; на пустом корешке (тоньше 80 страниц)
    // он раздувал бы элемент шире заявленной ширины (border-box не может ужаться
    // меньше суммы паддингов) — и лист вылезал бы за свою рамку.
    const sidePaddingMm = this.canPrintText ? SPINE_PADDING_MM : 0

    return `
      #${prefix} .cover-spine {
        width: ${this.widthMm}mm;
        height: 100%;
        flex: 0 0 ${this.widthMm}mm;
        padding: ${contentPaddingMm}mm ${sidePaddingMm}mm;
        box-sizing: border-box;
        background-color: ${this.bgColor};
        color: ${this.textColor};
        position: relative;
      }
      #${prefix} .spine-content {
        width: 100%;
        height: 100%;
        display: flex;
        flex-direction: column;
        justify-content: space-between;
        align-items: center;
      }
      #${prefix} .spine-title {
        writing-mode: vertical-rl;
        text-orientation: upright;
        letter-spacing: 0;
        font-family: 'Noto Serif JP', serif;
      }
      #${prefix} .spine-title.error-title {
        color: #ff5252;
      }
      #${prefix} .spine-title > span {
        display: block;
        font-size: ${this.fontSizeMm}mm;
        line-height: ${this.titleLineHeightMm}mm;
      }
      #${prefix} .spine-placeholder {
        flex-grow: 1;
      }
      #${prefix} .spine-bottom {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 5mm;
      }
      #${prefix} .spine-author {
        writing-mode: vertical-rl;
        text-orientation: upright;
        font-size: ${this.defaultFontSizeMm * AUTHOR_FONT_RATIO}mm;
        line-height: ${this.defaultFontSizeMm}mm;
        font-family: 'Noto Serif JP', serif;
      }
      /* Векторный логотип: цвета заданы fill-ами в разметке (см. spineLogo.js). */
      #${prefix} .spine-logo {
        display: block;
        width: ${this.widthMm - SPINE_PADDING_MM * 2}mm;
        height: ${(this.widthMm - SPINE_PADDING_MM * 2) * SPINE_LOGO_ASPECT}mm;
      }
      #${prefix} .semi-wide-number {
        writing-mode: horizontal-tb;
        text-combine-upright: all;
        display: inline;
      }
    `
  }
}

export {
  Spine,
  processNumbers,
  PT_TO_MM,
  SPINE_PADDING_MM,
  PRINT_TEXT_PADDING_MM,
  BLEED_MM,
  MIN_PAGES_FOR_SPINE_TEXT,
  DEFAULT_TEXT_COLOR,
  DEFAULT_BG_COLOR
}
